import React from 'react';
import { Box, Text } from '@mantine/core';
import cn from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import 'highlight.js/styles/github-dark.css';

import classes from './AssistantChat.module.css';

/**
 * Рендер-мапа для ReactMarkdown (создаётся один раз на модуль).
 * Единственный источник markdown-рендера сообщений ассистента в проекте.
 */
const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  p: ({ children }) => (
    <Text size='xs' mb={4} className={classes.markdownParagraph}>
      {children}
    </Text>
  ),
  h1: ({ children }) => (
    <Text size='sm' fw={700} mb='xs' mt='xs'>
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text size='xs' fw={600} mb={4} mt={4}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text size='xs' fw={600} mb={4} mt={4}>
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <Text component='ul' size='xs' mb={4} className={classes.markdownList}>
      {children}
    </Text>
  ),
  ol: ({ children }) => (
    <Text component='ol' size='xs' mb={4} className={classes.markdownList}>
      {children}
    </Text>
  ),
  li: ({ children }) => (
    <Text component='li' size='xs' mb={2}>
      {children}
    </Text>
  ),
  code: props => {
    const { inline, children, className } = props as {
      inline?: boolean;
      children: React.ReactNode;
      className?: string;
    };
    if (className && className.startsWith('language-')) {
      return <code className={className}>{children}</code>;
    }
    return (
      <Text
        component='code'
        size='xs'
        className={cn(classes.blockCode, inline && classes.inlineCode)}
      >
        {children}
      </Text>
    );
  },
  pre: ({ children }) => {
    const hasHighlight = React.Children.toArray(children).some(child => {
      if (React.isValidElement(child)) {
        const className = (child.props as { className?: string })?.className;
        return className?.startsWith('language-');
      }
      return false;
    });
    if (hasHighlight) {
      return (
        <Box
          component='pre'
          mb={4}
          className={cn(classes.preBase, classes.textBreak, classes.preHighlighted)}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component='pre'
        p={4}
        mb={4}
        className={cn(classes.preBase, classes.textBreak, classes.preDefault)}
      >
        {children}
      </Box>
    );
  },
  blockquote: ({ children }) => (
    <Box component='blockquote' pl={8} mb={4} className={classes.blockquote}>
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Text
      component='a'
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      size='xs'
      c='blue'
      className={classes.link}
    >
      {children}
    </Text>
  ),
  table: ({ children }) => (
    <Box component='table' className={classes.table}>
      {children}
    </Box>
  ),
  thead: ({ children }) => (
    <Box component='thead' className={classes.tableHead}>
      {children}
    </Box>
  ),
  th: ({ children }) => (
    <Text component='th' size='xs' p={4} className={cn(classes.tableBorder, classes.tableHeader)}>
      {children}
    </Text>
  ),
  td: ({ children }) => (
    <Text component='td' size='xs' p={4} className={classes.tableBorder}>
      {children}
    </Text>
  ),
};

/**
 * Markdown-текст сообщения ассистента (GFM + подсветка кода).
 */
export function AssistantMarkdown({ children }: { children: string }) {
  return (
    <Box className={cn(classes.markdownContainer, classes.textBreak)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={markdownComponents}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
